import React from 'react';
import { Button, ButtonProps, Dialog, DialogProps, Slide, SxProps, Theme, Typography } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { TransitionProps } from '@mui/material/transitions';
import { FONT_ENUM } from '../../types/global/fonts.types';
import Close from '@mui/icons-material/Close';

export interface IContentCreator {
    onClose?: () => void;
    open: boolean;
    onSubmit?: (...args: any) => void;
    children: JSX.Element;
    buttonEnabled?: boolean;
    buttonText?: string;
    paperSX?: SxProps<Theme>;
    dialogueSX?: SxProps<Theme>;
    buttonProps?: ButtonProps;
    dialogueProps?: DialogProps;
    header?: string;
    headerStyle?: React.CSSProperties;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});
/**
 * Custom Dialogue Modal that replaces The MUI Dialogue
 * @param props
 * @returns
 */

const DialogueModal: React.FC<IContentCreator> = props => {
    const {
        open,
        onClose,
        onSubmit,
        children,
        buttonEnabled,
        buttonText,
        paperSX,
        dialogueSX,
        buttonProps,
        header,
        headerStyle,
    } = props;
    const { themeStyle } = useThemeContext();
    const renderProjct = (
        <Dialog
            scroll="paper"
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            fullWidth={true}
            maxWidth={'lg'}
            slotProps={{
                backdrop: {
                    sx: {
                        opacity: 0.2,
                        backdropFilter: 'blur(8px)',
                    },
                },
            }}
            PaperProps={{
                sx: {
                    backgroundColor: theme[themeStyle].deep_grey_lighter,
                    width: '100vw',
                    maxWidth: {
                        xs: '90%',
                        sm: '500px',
                    },
                    borderRadius: 5,
                    padding: '30px',
                    margin: '0px',
                    marginTop: '30%',
                    border: `0.5px solid ${theme[themeStyle].darkGray60}`,
                    ...paperSX,
                },
            }}
            sx={{
                display: 'flex',
                height: { xs: '80%', sm: '70%', lg: '70%' },
                alignItems: 'center',
                justifyContent: 'center',
                outline: 'none',
                '& .MuiIconButton-root': {
                    display: 'none', // Hides the default close icon
                },
                ...dialogueSX,
            }}>
            <Button
                sx={{
                    position: 'absolute',
                    minWidth: '5px',
                    top: '2%',
                    right: '5%',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'flex-end',
                }}
                onClick={onClose}>
                <Close
                    sx={{
                        color: theme[themeStyle].tag_white,
                        width: '30px',
                        height: '30px',
                    }}
                />
            </Button>

            {!!header && (
                <Typography
                    style={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 18,
                        color: theme[themeStyle].tag_white,
                        marginTop: '15px',
                        ...headerStyle,
                    }}>
                    {header}
                </Typography>
            )}
            {children}
            {buttonEnabled && (
                <Button
                    onClick={onSubmit}
                    style={{
                        height: '52px',
                        borderRadius: 100,
                        fontSize: 16,
                        fontWeight: '600',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: '56px',
                    }}
                    sx={{
                        textDecoration: 'none',
                        backgroundColor: theme[themeStyle].primary_orange,
                        color: theme[themeStyle].pure_black,
                        textTransform: 'none',
                        width: '100%',
                        '&:hover': {
                            border: `2px solid ${theme[themeStyle].primary_orange}`,
                            backgroundColor: theme[themeStyle].pure_black,
                            color: theme[themeStyle].primary_orange,
                        },
                    }}
                    {...buttonProps}>
                    {buttonText || 'Submit'}
                </Button>
            )}
        </Dialog>
    );

    return renderProjct;
};

export { DialogueModal };
